<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { OrderStage, ProductionLot, ProductionOrder } from '../types/planning'

/**
 * Batch planning: how a released order is split into batches (lots).
 *
 * Lots and stages are real (`/production-orders/{id}/lots`, `…/stages`) and
 * creating a batch writes a real lot with `POST /production-orders/{id}/lots`.
 * The backend has no lot-size policy, so the form suggests the open quantity
 * rather than enforcing a standard batch size.
 */

const { t } = useI18n()
const auth = useAuthStore()
const { listProductionOrders, listOrderStages, listOrderLots, createLot } = usePlanningApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('production_orders.manage'))

const orders = ref<ProductionOrder[]>([])
const selectedOrderId = ref<number | ''>('')
const lots = ref<ProductionLot[]>([])
const stages = ref<OrderStage[]>([])
const loading = ref(true)
const detailLoading = ref(false)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const saving = ref(false)
const statusFilter = ref('in_progress')

const form = ref({
  stage_code: '',
  quantity: '' as number | string,
  notes: '',
})

const selectedOrder = computed(
  () => orders.value.find((order) => String(order.id) === String(selectedOrderId.value)) ?? null,
)

const batchedQuantity = computed(() =>
  lots.value
    .filter((lot) => lot.status !== 'cancelled')
    .reduce((sum, lot) => sum + Number(lot.initial_quantity ?? 0), 0),
)

const openQuantity = computed(() =>
  Math.max(0, Number(selectedOrder.value?.planned_quantity ?? 0) - batchedQuantity.value),
)

const activeLots = computed(() => lots.value.filter((lot) => lot.status === 'active'))

const stageOptions = computed(() =>
  stages.value.map((stage) => ({
    code: stage.stage_code,
    name: stage.stage_name,
    planned: stage.planned_quantity,
    completed: stage.completed_quantity,
  })),
)

async function loadOrders() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders({
      status: statusFilter.value || undefined,
      page: 1,
      per_page: 100,
    })
    orders.value = result.items
    if (
      selectedOrderId.value === '' ||
      !result.items.some((order) => String(order.id) === String(selectedOrderId.value))
    ) {
      selectedOrderId.value = result.items[0]?.id ?? ''
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadBatches() {
  lots.value = []
  stages.value = []
  if (!selectedOrderId.value) return
  detailLoading.value = true
  actionError.value = ''
  try {
    const [lotList, stageList] = await Promise.all([
      listOrderLots(Number(selectedOrderId.value)),
      listOrderStages(Number(selectedOrderId.value)),
    ])
    lots.value = lotList
    stages.value = stageList
    form.value.stage_code = stageList[0]?.stage_code ?? ''
    form.value.quantity = openQuantity.value || ''
    form.value.notes = ''
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    detailLoading.value = false
  }
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedOrderId.value) {
    actionError.value = t('planning.batches.invalidOrder')
    return
  }
  if (!form.value.stage_code) {
    actionError.value = t('planning.batches.invalidStage')
    return
  }
  const quantity = Number(form.value.quantity)
  if (!Number.isFinite(quantity) || quantity <= 0) {
    actionError.value = t('planning.batches.invalidQuantity')
    return
  }
  saving.value = true
  try {
    await createLot(Number(selectedOrderId.value), {
      stage_code: form.value.stage_code,
      quantity,
      notes: form.value.notes.trim() || undefined,
    })
    actionMessage.value = t('planning.batches.batchCreated')
    await loadBatches()
    await loadOrders()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

watch(selectedOrderId, () => {
  void loadBatches()
})

watch(statusFilter, () => {
  void loadOrders()
})

onMounted(() => {
  void loadOrders()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.batches.eyebrow') }}</p>
      <h2>{{ t('planning.batches.title') }}</h2>
      <p>{{ t('planning.batches.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="statusFilter" class="filter-select">
      <option value="">{{ t('planning.batches.allStatuses') }}</option>
      <option value="planned">{{ t('planning.orderStatus.planned') }}</option>
      <option value="in_progress">{{ t('planning.orderStatus.in_progress') }}</option>
      <option value="completed">{{ t('planning.orderStatus.completed') }}</option>
    </select>
    <select v-model="selectedOrderId" class="filter-select">
      <option value="">{{ t('planning.common.selectOrder') }}</option>
      <option v-for="order in orders" :key="order.id" :value="order.id">
        {{ order.mo_code }} — {{ order.product_name || '' }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="loadOrders">
      {{ t('planning.common.refresh') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="loadOrders">{{ t('planning.common.retry') }}</button>
  </div>

  <div v-else-if="!selectedOrder" class="state-message">
    <p>{{ t('planning.batches.noOrder') }}</p>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.common.planned') }}</span>
        <span class="summary-value">{{ selectedOrder.planned_quantity.toLocaleString() }}</span>
        <span class="summary-label">
          {{ t('planning.common.produced') }}: {{ selectedOrder.produced_quantity.toLocaleString() }}
        </span>
      </div>
      <div class="summary-card accent">
        <span class="summary-label">{{ t('planning.batches.batched') }}</span>
        <span class="summary-value">{{ batchedQuantity.toLocaleString() }}</span>
        <span class="summary-label">{{ t('planning.batches.lots', { count: lots.length }) }}</span>
      </div>
      <div class="summary-card" :class="{ attention: activeLots.length > 0 }">
        <span class="summary-label">{{ t('planning.batches.openQuantity') }}</span>
        <span class="summary-value">{{ openQuantity.toLocaleString() }}</span>
        <span class="summary-label">{{ t('planning.batches.openHint') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.batches.activeLots') }}</span>
        <span class="summary-value">{{ activeLots.length }}</span>
        <span class="summary-label">
          {{ t('planning.common.department') }}: {{ selectedOrder.department_name || '—' }}
        </span>
      </div>
    </div>

    <div class="detail-grid">
      <section v-if="canManage" class="panel">
        <div class="panel-header">
          <h3>{{ t('planning.batches.newBatch') }}</h3>
        </div>
        <form class="form-grid tight" @submit.prevent="submit">
          <div class="field field-wide">
            <label>{{ t('planning.common.stage') }}</label>
            <select v-model="form.stage_code" :disabled="stageOptions.length === 0">
              <option value="">{{ t('planning.common.selectStage') }}</option>
              <option v-for="stage in stageOptions" :key="stage.code" :value="stage.code">
                {{ stage.name }} — {{ stage.completed.toLocaleString() }} / {{ stage.planned.toLocaleString() }}
              </option>
            </select>
          </div>
          <div class="field">
            <label>{{ t('planning.batches.quantity') }}</label>
            <input v-model="form.quantity" type="number" min="0" step="1">
            <small>{{ t('planning.batches.openQuantity') }}: {{ openQuantity.toLocaleString() }}</small>
          </div>
          <div class="field">
            <label>{{ t('planning.common.notes') }} <small>{{ t('planning.common.optional') }}</small></label>
            <input v-model.trim="form.notes" type="text">
          </div>
          <div class="field field-wide">
            <button class="btn-primary" type="submit" :disabled="saving">
              {{ saving ? t('planning.common.saving') : t('planning.batches.createBatch') }}
            </button>
          </div>
        </form>
        <p class="empty-inline">{{ t('planning.batches.policyHint') }}</p>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('planning.batches.lotList') }}</h3>
        </div>

        <div v-if="detailLoading" class="empty-inline">{{ t('planning.common.loading') }}</div>

        <div v-else-if="lots.length === 0" class="empty-inline">
          {{ t('planning.batches.noLots') }}
        </div>

        <div v-else class="table-scroll">
          <table class="data-table compact">
            <thead>
              <tr>
                <th>{{ t('planning.common.lot') }}</th>
                <th>{{ t('planning.common.status') }}</th>
                <th>{{ t('planning.common.stage') }}</th>
                <th class="cell-num">{{ t('planning.batches.initial') }}</th>
                <th class="cell-num">{{ t('planning.batches.current') }}</th>
                <th>{{ t('planning.batches.created') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lot in lots" :key="lot.id">
                <td class="cell-code">{{ lot.lot_number }}</td>
                <td>
                  <span
                    class="status-badge"
                    :class="lot.status === 'active' ? 'status-ok' : lot.status === 'completed' ? 'status-info' : 'status-critical'"
                  >
                    {{ t(`planning.lotStatus.${lot.status}`) }}
                  </span>
                </td>
                <td>{{ lot.current_stage_code }}</td>
                <td class="cell-num">{{ Number(lot.initial_quantity).toLocaleString() }}</td>
                <td class="cell-num">{{ Number(lot.current_quantity).toLocaleString() }}</td>
                <td class="cell-date">{{ formatDisplayDateTime(lot.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </template>
</template>
